/**
 * Kanban Page
 *
 * Full-featured Kanban board with:
 * - Multiple saved views
 * - Customizable columns (filter-based or stages)
 * - Resizable columns
 * - Customizable card display
 * - Drag-drop between columns
 */

import { useState, useCallback, useEffect } from 'react';
import { Typography, Space, Button, Spin } from 'antd';
import { ReloadOutlined, PlusOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { instancesApi } from '@api/index';
import { kanbanApi } from '@api/kanban';
import { CustomKanbanBoard, KanbanViewSelector } from '@components/kanban';
import { WorkPackageDrawer } from '@components/drawers';
import type { ProcessInstance } from '@/types';

const { Title } = Typography;

export default function KanbanPage() {
  const queryClient = useQueryClient();
  const [selectedViewId, setSelectedViewId] = useState<string | undefined>();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState<string | undefined>();
  const [, setCreateInColumnId] = useState<string | undefined>();

  // Fetch all views to find default or first
  const { data: views = [], isLoading: loadingViews } = useQuery({
    queryKey: ['kanban-views'],
    queryFn: () => kanbanApi.listViews(),
  });

  // Auto-select default view or first available
  useEffect(() => {
    if (!selectedViewId && views.length > 0) {
      const instanceViews = views.filter(v => v.entityType === 'instances');
      const defaultView = instanceViews.find(v => v.isDefault);
      setSelectedViewId(defaultView?.id || instanceViews[0]?.id);
    }
  }, [views, selectedViewId]);

  // Fetch selected view with columns
  const { data: currentView, isLoading: loadingView, refetch: refetchView } = useQuery({
    queryKey: ['kanban-view', selectedViewId],
    queryFn: () => kanbanApi.getView(selectedViewId!),
    enabled: !!selectedViewId,
  });

  // Fetch all instances for the board
  const { data: instances = [], isLoading: loadingInstances, refetch: refetchInstances } = useQuery({
    queryKey: ['instances', 'kanban'],
    queryFn: () => instancesApi.list({
      status: ['pending', 'in_progress', 'postponed'],
    }),
  });

  // Update instance mutation (for moving cards)
  const updateInstanceMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => instancesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['instances'] });
    },
  });

  // Handle item click
  const handleItemClick = useCallback((item: ProcessInstance) => {
    setSelectedItemId(item.id);
    setCreateInColumnId(undefined);
    setDrawerOpen(true);
  }, []);

  // Handle item move between columns
  const handleItemMove = useCallback(async (itemId: string, _fromColumnId: string, toColumnId: string) => {
    if (!currentView) return;

    // Find the target column
    const targetColumn = currentView.columns?.find(c => c.id === toColumnId);
    if (!targetColumn) return;

    // Apply drop action from target column
    const dropAction = targetColumn.dropAction;
    const updateData: any = {};

    if (dropAction?.setStatus) {
      updateData.status = dropAction.setStatus;
    }
    if (dropAction?.setCategory) {
      updateData.categoryId = dropAction.setCategory;
    }

    if (Object.keys(updateData).length > 0) {
      await updateInstanceMutation.mutateAsync({ id: itemId, data: updateData });
    }
  }, [currentView, updateInstanceMutation]);

  // Handle add item in column
  const handleAddItem = useCallback((columnId: string) => {
    setSelectedItemId(undefined);
    setCreateInColumnId(columnId);
    setDrawerOpen(true);
  }, []);

  // Handle drawer close
  const handleCloseDrawer = useCallback(() => {
    setDrawerOpen(false);
    setSelectedItemId(undefined);
    setCreateInColumnId(undefined);
  }, []);

  // Handle drawer save
  const handleDrawerSaved = useCallback(() => {
    refetchInstances();
  }, [refetchInstances]);

  // Handle view change (refresh)
  const handleViewChange = useCallback(() => {
    refetchView();
  }, [refetchView]);

  // Refresh all data
  const handleRefresh = useCallback(() => {
    refetchInstances();
    refetchView();
  }, [refetchInstances, refetchView]);

  const isLoading = loadingViews || loadingView || loadingInstances;

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 16px',
        borderBottom: '1px solid var(--border-default, #dde3ec)',
        background: 'var(--surface-1, #fff)',
        flexShrink: 0,
      }}>
        <Space>
          <Title level={4} style={{ margin: 0 }}>Kanban</Title>
          <KanbanViewSelector
            selectedViewId={selectedViewId}
            onViewSelect={setSelectedViewId}
            entityType="instances"
          />
        </Space>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            loading={isLoading}
          >
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => handleAddItem('')}
          >
            New Item
          </Button>
        </Space>
      </div>

      {/* Board */}
      <div style={{ flex: 1, overflow: 'hidden' }}>
        {isLoading && !currentView ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Spin size="large" />
          </div>
        ) : currentView ? (
          <CustomKanbanBoard
            view={currentView}
            items={instances}
            loading={loadingInstances}
            onItemClick={handleItemClick}
            onItemMove={handleItemMove}
            onAddItem={handleAddItem}
            onViewChange={handleViewChange}
          />
        ) : (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            gap: 16,
          }}>
            <Typography.Text type="secondary">
              No Kanban views found. Create one to get started.
            </Typography.Text>
          </div>
        )}
      </div>

      {/* Item Drawer */}
      <WorkPackageDrawer
        open={drawerOpen}
        itemId={selectedItemId}
        mode={selectedItemId ? 'edit' : 'create'}
        isTemplate={false}
        onClose={handleCloseDrawer}
        onSaved={handleDrawerSaved}
      />
    </div>
  );
}
